import {ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfigurationService } from '../../../../../configuration/store/configuration.service';
import { ConfigurationCreationScope, ConfigurationFn, ConfigurationObject, } from '../../../../../configuration/store/configuration.datamodel';
import { ActorObject, ActorType } from '../../../../../shared/datamodel';
import { PermissionsService } from '../../../../../user/permissions/permissions.service';
import { PermissionsType } from '../../../../../user/permissions/permissions.datamodel';
import { UserProfile } from '../../../../../user/user.datamodel';
import { UserService } from '../../../../../user/user.service';
import {
  ParameterContainerComponent
} from '../../../../../shared/list/details/configuration-parameters/parameter-container/parameter-container.component';
import {
  ParameterContentValue
} from '../../../../../shared/list/details/configuration-parameters/parameters-group/parameters.model';
import {
  ConfigurationParametersModule
} from '../../../../../shared/list/details/configuration-parameters/configuration-parameters.module';
import {Subscription} from "rxjs";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "ama-ng-configuration-modal",
  templateUrl: "./configurationFormModal.component.html",
  styleUrls: ["./configurationFormModal.component.scss"],
  imports: [
    ReactiveFormsModule,
    NgbTooltip,
    ConfigurationParametersModule
  ],
  standalone: true
})
export class ConfigurationModalComponent implements OnInit, OnDestroy {
  @ViewChild(ParameterContainerComponent) parameterContainerComponent!: ParameterContainerComponent;

  @Input() configuration!: ConfigurationObject;
  @Input() parent!: ConfigurationObject;
  @Input() configParameters!: ParameterContentValue;
  @Input() edit!: boolean;
  @Input() displayDynamicForm!: boolean;
  @Input() isSimpleViewActive!: boolean;
  @Input() canCreateGlobalConfig = false;

  configurationForm!: UntypedFormGroup;
  selectedScope: ConfigurationCreationScope = ConfigurationCreationScope.ORGANISATION;
  readonly ConfigurationCreationScope = ConfigurationCreationScope;
  protected parentActor?: ActorObject;
  private readonly sub: Subscription;

  private readonly user: UserProfile;

  get organisationScopeTarget(): string {
    return this.buildOrgActor().actorName;
  }

  constructor(
    public activeModal: NgbActiveModal,
    private readonly configService: ConfigurationService,
    private readonly fb: UntypedFormBuilder,
    private readonly permissionsService: PermissionsService,
    private readonly userService: UserService,
  ) {
    this.user = this.userService.getUser();
    this.sub = this.configService.activeParent$.subscribe({
      next: (parent) => {
        if (parent?.actor && parent?.actor.actorType === ActorType.ORGANISATION) {
          this.parentActor = parent.actor;
        }
      }
    });
  }

  ngOnInit() {
    this.configurationForm = this.fb.group(
      ConfigurationFn.getValidatorsObject(this.configuration)
    );
    this.configurationForm.get("name")?.setValidators([Validators.required]);

    if (this.configuration.actor?.actorType === ActorType.PRODUCT && this.configuration.actor?.actorName === 'SELL_CONNECT') {
      this.selectedScope = ConfigurationCreationScope.GLOBAL;
    }
  }

  /**
   * Switches the configuration scope between Organisation and Global.
   * Updates the actor (and parentActor) form controls accordingly.
   */
  onScopeChange(scope: ConfigurationCreationScope): void {
    if (scope === ConfigurationCreationScope.GLOBAL && !this.canCreateGlobalConfig) {
      return;
    }

    this.selectedScope = scope;
    if (scope === ConfigurationCreationScope.GLOBAL) {
      this.configurationForm.get('actor')?.setValue({ actorName: 'SELL_CONNECT', actorType: ActorType.PRODUCT });
      this.configurationForm.get('parentActor')?.setValue(null);
    } else {
      this.configurationForm.get('actor')?.setValue(this.buildOrgActor());
      this.configurationForm.get('parentActor')?.setValue(this.buildOrgParentActor());
    }
  }

  /**
   * Submits the configuration content form
   */
  onSubmit() {
    if (this.displayDynamicForm) {
      this.parameterContainerComponent.submit();
    }

    const config = this.configurationForm.getRawValue() as ConfigurationObject;
    let errors = !!this.configurationForm.get("content")?.errors;
    // Check name errors
    if (!config.name) {
      errors = true;
      this.configurationForm.get("name")?.markAsDirty();
      this.configurationForm.get("name")?.setErrors({ required: true });
    }
    // Check content errors
    const error = this.configService.validateRedefinedConfigurationContent(
      config.content ?? '',
      this.parent.content ?? ''
    );
    if (error) {
      errors = true;
      const contentErrors = Object.assign(
        {},
        this.configurationForm.get("content")?.errors,
        error
      );
      this.configurationForm.get("content")?.setErrors(contentErrors);
    }
    // Create/update the redefined configuration
    if (!errors) {
      this.configService.redefineConfigurationAction(config);
      this.activeModal.close();
    }
  }

  private buildOrgActor(): ActorObject {
    return this.permissionsService.isUserAuthorized(PermissionsType.MANAGE_MARKET_CATALOG)
      ? this.parentActor ?? { actorName: this.user.organizationId, actorType: ActorType.ORGANISATION }
      : { actorName: this.user.officeId, actorType: ActorType.OFFICE };
  }

  private buildOrgParentActor(): ActorObject | null {
    return this.permissionsService.isUserAuthorized(PermissionsType.MANAGE_MARKET_CATALOG)
      ? null
      : this.parentActor ?? { actorName: this.user.organizationId, actorType: ActorType.ORGANISATION };
  }

  /**
   * Toggles the parameter component view
   */
  toggleView() {
    this.isSimpleViewActive = this.parameterContainerComponent.toggleView();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
